import { Component } from 'react'
import { createRoot, Root } from 'react-dom/client'

import styled, { ThemeProvider } from 'styled-components'

import theme from 'modules/App/theme'
import { UnknownRecord } from 'types/global'
import { Z_INDEX } from 'utils/constants'

import ToastMessage from './ToastMessage'
import { KeyType, ToastProps } from './types'

const notificationRootByContainer = new WeakMap<Element, Root>()

function ensureNotificationRoot(container: Element): Root {
  let root = notificationRootByContainer.get(container)
  if (!root) {
    root = createRoot(container)
    notificationRootByContainer.set(container, root)
  }
  return root
}

const getTime = () => Date.now()
const getUuid = () => `Id_${Math.random().toString(36).replace('.', '')}_${getTime()}`

const NotificationWrapper = styled.div`
  z-index: ${Z_INDEX.notification};
  display: flex;
  flex-direction: column;
  align-items: center;
  position: fixed;
  width: 100%;
  top: 8px;
  pointer-events: none;
`

class Notification extends Component<UnknownRecord, { notices: Array<ToastProps & { noticeKey: string }> }> {
  constructor(props: UnknownRecord) {
    super(props)
    this.state = {
      notices: [],
    }
  }

  // Both updates are functional, not built from `this.state`. React 18 batches setState,
  // so two toasts raised in the same tick (a save's own success plus a follow-up, say)
  // both read the list from before either landed, and the second overwrote the first.
  add = (newNotice: ToastProps) => {
    const noticeKey = getUuid()
    this.setState((state) => ({
      notices: [...state.notices, { ...newNotice, noticeKey }],
    }))
  }

  remove = (key: KeyType) => {
    this.setState((state) => ({
      notices: state.notices.filter((notice) =>
        key.givenKey ? notice.givenKey !== key.givenKey : notice.noticeKey !== key.noticeKey,
      ),
    }))
  }

  /**
   * This function is also used to mount Notification host component to DOM
   */
  static newInstanced = (properties: UnknownRecord, callback: (n: Notification) => void) => {
    // Ref function is used to set the instance of Notification Component
    const ref = (notificationInstance: Notification) => {
      callback(notificationInstance)
    }

    // Setup  host component
    const host = document.getElementById('notification')
    if (host) {
      ensureNotificationRoot(host).render(<Notification ref={ref} {...properties} />)
    } else {
      // Create a div element and append it to document body
      const newHost = document.createElement('div')
      newHost.setAttribute('id', 'notification')
      document.body.appendChild(newHost)

      // Mount host to the div element that was created
      ensureNotificationRoot(newHost).render(<Notification {...properties} ref={ref} />)
    }
  }

  render() {
    return (
      <ThemeProvider theme={theme}>
        <NotificationWrapper>
          {this.state.notices.map((notice) => (
            <ToastMessage {...notice} key={notice.noticeKey} onClose={this.remove} />
          ))}
        </NotificationWrapper>
      </ThemeProvider>
    )
  }
}

export default Notification
